import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Heart, MessageCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Celebration sound URL (royalty-free party horn)
const CELEBRATION_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2013/2013-preview.mp3';

const MatchCelebration = ({ 
  isOpen, 
  onClose, 
  currentUserPhoto, 
  matchedUserPhoto, 
  matchedUserName,
  compatibilityScore,
  onStartChat 
}) => {
  const audioRef = useRef(null);
  const [soundPlayed, setSoundPlayed] = useState(false);

  useEffect(() => {
    if (isOpen && !soundPlayed) {
      // Fire confetti
      fireConfetti();
      
      // Play celebration sound
      playSound();
      setSoundPlayed(true);
    }
    
    if (!isOpen) {
      setSoundPlayed(false);
    }
  }, [isOpen, soundPlayed]);

  const fireConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    
    // RecoveryMatch colors - Blue and Yellow/Gold
    const colors = ['#1C3A7A', '#FAB710', '#FFD700', '#2563EB', '#FCD34D'];

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      // Confetti from left
      confetti({
        particleCount: Math.floor(particleCount),
        startVelocity: 30,
        spread: 360,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: colors,
        shapes: ['circle', 'square'],
        gravity: 0.8,
        scalar: randomInRange(0.8, 1.2),
        drift: randomInRange(-0.5, 0.5),
      });

      // Confetti from right
      confetti({
        particleCount: Math.floor(particleCount),
        startVelocity: 30,
        spread: 360,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: colors,
        shapes: ['circle', 'square'],
        gravity: 0.8,
        scalar: randomInRange(0.8, 1.2),
        drift: randomInRange(-0.5, 0.5),
      });
    }, 250);

    // Initial burst from center
    confetti({
      particleCount: 100,
      spread: 100,
      origin: { x: 0.5, y: 0.5 },
      colors: colors,
      shapes: ['circle', 'square'],
      startVelocity: 45,
    });
  };

  const playSound = () => {
    try {
      if (audioRef.current) {
        audioRef.current.volume = 0.5;
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
    } catch (error) {
      console.log('Sound play error:', error);
    }
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    onClose();
  };

  const handleStartChat = () => {
    handleClose();
    if (onStartChat) onStartChat();
  };

  // Default photos if none provided
  const leftPhoto = currentUserPhoto || 'https://via.placeholder.com/150/1C3A7A/FFFFFF?text=You';
  const rightPhoto = matchedUserPhoto || 'https://via.placeholder.com/150/FAB710/FFFFFF?text=Match';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
        >
          {/* Audio element */}
          <audio ref={audioRef} src={CELEBRATION_SOUND_URL} preload="auto" />
          
          {/* Background gradient animation */}
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              background: 'radial-gradient(ellipse at center, rgba(28, 58, 122, 0.4) 0%, rgba(0, 0, 0, 0.9) 70%)',
            }}
          />

          {/* Close button */}
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            onClick={handleClose}
            className="absolute top-6 right-6 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </motion.button>

          {/* Main content */}
          <div className="relative z-10 flex flex-col items-center justify-center px-4">
            {/* "It's a Match!" text */}
            <motion.div
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ 
                type: 'spring', 
                stiffness: 200, 
                damping: 15,
                delay: 0.2 
              }}
              className="mb-8"
            >
              <h1 
                className="text-5xl md:text-7xl font-bold text-center"
                style={{
                  background: 'linear-gradient(135deg, #FAB710 0%, #FFD700 50%, #FAB710 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 40px rgba(250, 183, 16, 0.5)',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
              >
                It's a Match!
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-center text-white/80 mt-2 text-lg"
              >
                You and {matchedUserName || 'your match'} liked each other
              </motion.p>
            </motion.div>

            {/* Profile pictures container */}
            <div className="relative flex items-center justify-center mb-8 h-48">
              {/* Left profile (current user) - comes from left */}
              <motion.div
                initial={{ x: -300, opacity: 0, rotate: -20 }}
                animate={{ x: 0, opacity: 1, rotate: -5 }}
                transition={{ 
                  type: 'spring', 
                  stiffness: 150, 
                  damping: 20,
                  delay: 0.3 
                }}
                className="relative"
              >
                <div 
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 shadow-2xl"
                  style={{ 
                    borderColor: '#1C3A7A',
                    boxShadow: '0 0 30px rgba(28, 58, 122, 0.6)'
                  }}
                >
                  <img 
                    src={leftPhoto} 
                    alt="You" 
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Glow effect */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 0.8, 0.5]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(28, 58, 122, 0.4) 0%, transparent 70%)',
                    filter: 'blur(10px)',
                  }}
                />
              </motion.div>

              {/* Heart in the middle */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  type: 'spring',
                  stiffness: 300,
                  damping: 15,
                  delay: 0.8 
                }}
                className="relative z-10 mx-[-20px]"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                  }}
                  transition={{ 
                    duration: 0.8,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                >
                  <Heart 
                    className="w-16 h-16 md:w-20 md:h-20" 
                    fill="#FAB710" 
                    color="#FAB710"
                    style={{
                      filter: 'drop-shadow(0 0 20px rgba(250, 183, 16, 0.8))'
                    }}
                  />
                </motion.div>
              </motion.div>

              {/* Right profile (matched user) - comes from right */}
              <motion.div
                initial={{ x: 300, opacity: 0, rotate: 20 }}
                animate={{ x: 0, opacity: 1, rotate: 5 }}
                transition={{ 
                  type: 'spring', 
                  stiffness: 150, 
                  damping: 20,
                  delay: 0.3 
                }}
                className="relative"
              >
                <div 
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 shadow-2xl"
                  style={{ 
                    borderColor: '#FAB710',
                    boxShadow: '0 0 30px rgba(250, 183, 16, 0.6)'
                  }}
                >
                  <img 
                    src={rightPhoto} 
                    alt={matchedUserName || 'Match'} 
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Glow effect */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 0.8, 0.5]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.5
                  }}
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(250, 183, 16, 0.4) 0%, transparent 70%)',
                    filter: 'blur(10px)',
                  }}
                />
              </motion.div>
            </div>

            {/* Compatibility score */}
            {compatibilityScore && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="mb-8 px-6 py-3 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(28, 58, 122, 0.8) 0%, rgba(28, 58, 122, 0.6) 100%)',
                  border: '1px solid rgba(250, 183, 16, 0.5)',
                }}
              >
                <span className="text-white/80">Compatibility: </span>
                <span 
                  className="font-bold text-xl"
                  style={{ color: '#FAB710' }}
                >
                  {compatibilityScore}%
                </span>
              </motion.div>
            )}

            {/* Action buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="flex flex-col sm:flex-row gap-4 w-full max-w-md px-4"
            >
              <Button
                onClick={handleStartChat}
                className="flex-1 py-6 text-lg font-semibold rounded-full shadow-lg transition-all hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #FAB710 0%, #F59E0B 100%)',
                  color: '#1C3A7A',
                }}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Send a Message
              </Button>
              <Button
                onClick={handleClose}
                variant="outline"
                className="flex-1 py-6 text-lg font-semibold rounded-full border-2 transition-all hover:scale-105"
                style={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                Keep Swiping
              </Button>
            </motion.div>

            {/* Sparkle decorations */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: i * 0.3,
                  ease: 'easeInOut',
                }}
                style={{
                  top: `${20 + Math.random() * 60}%`,
                  left: `${10 + Math.random() * 80}%`,
                }}
              >
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none"
                  style={{ color: i % 2 === 0 ? '#FAB710' : '#1C3A7A' }}
                >
                  <path
                    d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
                    fill="currentColor"
                  />
                </svg>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MatchCelebration;
